import React from 'react';
import { useImmer } from 'use-immer';
import { AppStateContext, defaultAppState } from '@/shared/contexts';
import {
  localStorageLabels,
  syncStateIntoStorageService,
  syncStorageIntoStateService,
} from '@/utils';

export const AppStateContextProvider = ({ children }) => {
  const { appState, setAppState } = useAppStateContextProvider();

  return (
    <AppStateContext.Provider value={{ appState, setAppState }}>
      {children}
    </AppStateContext.Provider>
  );
};

// Custom hook to manage and sync app state with localStorage
const useAppStateContextProvider = () => {
  // Use Immer for immutable state updates
  const [appState, setAppState] = useImmer(defaultAppState);

  // Helper to validate TID (must be a non-empty string)
  const validateTid = (tid) =>
    typeof tid === 'string' && tid?.trim().length > 0;

  // Sync TID between state and localStorage
  React.useMemo(() => {
    const tid = appState?.tid;
    if (validateTid(tid))
      // If TID is valid, save to localStorage
      syncStateIntoStorageService({ value: tid, key: localStorageLabels.TID });
    else {
      // Otherwise, try to load from localStorage into state
      const tid = syncStorageIntoStateService(localStorageLabels.TID);
      if (validateTid(tid))
        setAppState((draft) => {
          draft.tid = tid;
        });
    }
  }, [appState?.tid]);

  // Sync localData between state and localStorage
  React.useMemo(() => {
    const localData = appState?.localData;

    if (localData?.config?.id) {
      // If localData has a config id, save to localStorage
      syncStateIntoStorageService({
        key: localStorageLabels.LOCAL_DATA,
        value: localData,
      });
    } else {
      // Otherwise, try to load from localStorage into state
      const storedLocalData = syncStorageIntoStateService(
        localStorageLabels.LOCAL_DATA,
      );

      if (storedLocalData)
        setAppState((draft) => {
          draft.localData = storedLocalData;
        });
    }
  }, [appState?.localData]);

  // Sync screenInfo between state and localStorage
  React.useMemo(() => {
    const screenInfo = appState?.screenInfo;

    if (screenInfo?.screenId) {
      // If screenInfo has a screenId, save to localStorage
      syncStateIntoStorageService({
        key: localStorageLabels.DEVICE_INFO,
        value: screenInfo,
      });
    } else {
      // Otherwise, try to load from localStorage into state
      const storedScreenInfo = syncStorageIntoStateService(
        localStorageLabels.DEVICE_INFO,
      );

      if (storedScreenInfo)
        setAppState((draft) => {
          draft.screenInfo = storedScreenInfo;
        });
    }
  }, [appState?.screenInfo]);

  // Sync screenMeta between state and localStorage
  React.useMemo(() => {
    const screenMeta = appState?.screenMeta;

    if (screenMeta) {
      syncStateIntoStorageService({
        key: localStorageLabels.DEVICE_META,
        value: screenMeta,
      });
    } else {
      const storedScreenMeta = syncStorageIntoStateService(
        localStorageLabels.DEVICE_META,
      );

      if (storedScreenMeta)
        setAppState((draft) => {
          draft.screenMeta = storedScreenMeta;
        });
    }
  }, [appState?.screenMeta]);

  // Return state and updater for context provider
  return { appState, setAppState };
};
